import React from 'react'
import {useAppData} from '../../../providers/AppDataProvider'
import {NewBankBill, nextGenerationAfterPayment} from '../utils/bankBillWorkflow'
import {addFrequency} from '../../../utils/recurrence'

export function NewBankBillPanel({
  draft,
  onChange,
  amount,
  paymentDate,
  today,
  busy,
  existingTemplates,
}: {
  draft: NewBankBill
  onChange: (draft: NewBankBill) => void
  amount: number
  paymentDate: string
  today: string
  busy: boolean
  existingTemplates: {contactId: string | null; contactName: string | null}[]
}) {
  const {customers} = useAppData()
  const contacts = (customers || []).filter((c) => c.id && (c.name || c.company))
  const change = (patch: Partial<NewBankBill>) => onChange({...draft, ...patch})
  const hasTemplate =
    draft.contactName &&
    existingTemplates.some(
      (t) =>
        (draft.contactId && t.contactId === draft.contactId) ||
        t.contactName?.toLowerCase() === draft.contactName.toLowerCase()
    )
  let futureDates: string[] = []
  try {
    if (draft.nextDate) {
      futureDates = [draft.nextDate]
      const anchor =
        draft.nextDate === nextGenerationAfterPayment(paymentDate, draft.frequency, today)
          ? paymentDate
          : draft.nextDate
      for (let n = 0; n < 2; n++)
        futureDates.push(addFrequency(futureDates[n], draft.frequency, anchor))
    }
  } catch {
    /* Form validation prevents saving an invalid date. */
  }
  return (
    <section className='bank-panel bank-new-bill'>
      <h4>Create a bill from this payment</h4>
      <p>The bill and its payment will be saved together when you confirm reconciliation.</p>
      <div className='bank-form-grid'>
        <label className='bank-field'>
          Supplier contact
          <select
            className='form-select'
            disabled={busy}
            value={draft.contactId}
            onChange={(e) => {
              const contact = contacts.find((c) => c.id === e.target.value)
              change({
                contactId: e.target.value,
                contactName: contact ? contact.name || contact.company || '' : '',
              })
            }}
          >
            <option value=''>Enter supplier name</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.company}
              </option>
            ))}
          </select>
        </label>
        {!draft.contactId && (
          <label className='bank-field'>
            Supplier name
            <input
              className='form-control'
              value={draft.contactName}
              maxLength={200}
              disabled={busy}
              onChange={(e) => change({contactName: e.target.value})}
            />
          </label>
        )}
        <label className='bank-field'>
          Supplier invoice reference
          <input
            className='form-control'
            value={draft.reference}
            maxLength={500}
            disabled={busy}
            onChange={(e) => change({reference: e.target.value})}
          />
        </label>
        <label className='bank-field'>
          Tax treatment
          <select
            className='form-select'
            value={draft.taxType}
            disabled={busy}
            onChange={(e) => change({taxType: e.target.value as NewBankBill['taxType']})}
          >
            <option value=''>Choose from supplier invoice</option>
            <option value='GST'>Includes GST</option>
            <option value='GST_FREE'>GST free</option>
            <option value='NONE'>No GST</option>
          </select>
        </label>
      </div>
      <label className='bank-field'>
        Bill description
        <textarea
          className='form-control'
          value={draft.description}
          maxLength={2000}
          disabled={busy}
          onChange={(e) => change({description: e.target.value})}
        />
      </label>
      <p>
        <strong>
          {new Intl.NumberFormat('en-AU', {style: 'currency', currency: 'AUD'}).format(amount)}
        </strong>{' '}
        total · Bill and payment dated {paymentDate}.
      </p>
      <label className='bank-field'>
        Bill type
        <select
          className='form-select'
          value={draft.recurring ? 'recurring' : 'once'}
          disabled={busy}
          onChange={(e) => change({recurring: e.target.value === 'recurring'})}
        >
          <option value='once'>One-off bill</option>
          <option value='recurring'>Recurring bill</option>
        </select>
      </label>
      {draft.recurring && (
        <>
          {hasTemplate && (
            <p className='bank-notice'>
              This supplier already has a recurring bill template. Check it before creating another
              schedule.
            </p>
          )}
          <div className='bank-form-grid'>
            <label className='bank-field'>
              Repeat every
              <select
                className='form-select'
                value={draft.frequency}
                disabled={busy}
                onChange={(e) =>
                  change({
                    frequency: e.target.value,
                    nextDate: nextGenerationAfterPayment(paymentDate, e.target.value, today),
                  })
                }
              >
                <option value='WEEKLY'>Week</option>
                <option value='MONTHLY'>Month</option>
                <option value='QUARTERLY'>Quarter</option>
                <option value='YEARLY'>Year</option>
              </select>
            </label>
            <label className='bank-field'>
              First future generation
              <input
                type='date'
                className='form-control'
                value={draft.nextDate}
                min={today}
                disabled={busy}
                onChange={(e) => change({nextDate: e.target.value})}
              />
            </label>
          </div>
          <p className='bank-date-preview'>{futureDates.join(' → ')}</p>
          <small>
            Creates an active template using this bill amount and tax treatment. The paid occurrence
            is recorded once; automatic generation begins on the date above.
          </small>
        </>
      )}
    </section>
  )
}
